/** Contabilidad → Libro mayor: los apuntes de una cuenta con su saldo acumulado. */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { usePgcIssuer, useCanSeeLedger } from '../../lib/ctb'
import CtbHeader from '../../components/ctb/CtbHeader'
import { AccountDatalist } from '../../components/ctb/AccountInput'
import { apiMessage } from '../../components/ctb/EntryModal'
import { Note, selectCls, td, tdR, th, thR } from '../../components/accounting/common'
import type { CtbAccountDto, CtbLedgerResponse } from '@toga/shared'

/** Saldo con su naturaleza: «1.210,00 € D» (deudor) o «H» (acreedor). */
export const side = (cents: number): string =>
  cents === 0 ? formatCents(0) : `${formatCents(Math.abs(cents))} ${cents > 0 ? 'D' : 'H'}`

export default function LedgerPage() {
  const canSee = useCanSeeLedger()
  const { pgc, issuerId, setIssuerId, loaded } = usePgcIssuer()
  const [year, setYear] = useState(new Date().getFullYear())
  const [code, setCode] = useState('')
  const [accounts, setAccounts] = useState<CtbAccountDto[]>([])
  const [data, setData] = useState<CtbLedgerResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!issuerId) return
    api
      .get<{ items: CtbAccountDto[] }>(`/ctb/accounts?issuerId=${issuerId}`)
      .then((r) => setAccounts(r.items))
      .catch(() => setAccounts([]))
  }, [issuerId])

  useEffect(() => {
    setData(null)
    setError(null)
    if (!issuerId || !code || !accounts.some((a) => a.code === code)) return
    let alive = true
    setLoading(true)
    api
      .get<CtbLedgerResponse>(`/ctb/ledger?issuerId=${issuerId}&account=${code}&year=${year}`)
      .then((r) => alive && setData(r))
      .catch((err) => alive && setError(apiMessage(err, 'No se pudo cargar el mayor')))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [issuerId, code, year, accounts])

  if (!canSee)
    return (
      <p className="t-muted text-sm">El libro mayor es solo para administradores y abogados.</p>
    )
  const name = accounts.find((a) => a.code === code)?.name

  return (
    <div className="space-y-5 animate-slideUp">
      <CtbHeader
        title="Contabilidad · Libro mayor"
        description="Los apuntes de una cuenta del ejercicio, con el saldo acumulado tras cada uno. D = saldo deudor, H = acreedor."
        pgc={pgc}
        loaded={loaded}
        issuerId={issuerId}
        onIssuer={setIssuerId}
        year={year}
        onYear={setYear}
      >
        <AccountDatalist
          id="ledger-accounts"
          accounts={accounts.map((a) => ({ ...a, isPostable: true }))}
        />
        <input
          list="ledger-accounts"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="Cuenta (p. ej. 572)"
          inputMode="numeric"
          className={`${selectCls} w-44 font-mono`}
          aria-label="Cuenta"
        />
        {name && <span className="text-xs t-muted">{name}</span>}
      </CtbHeader>

      {error && (
        <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      {issuerId && (
        <section className="surface overflow-hidden">
          {!code ? (
            <div className="px-5 py-10 text-center t-muted text-sm">
              Elige una cuenta para ver su mayor.
            </div>
          ) : !accounts.some((a) => a.code === code) ? (
            <div className="px-5 py-10 text-center t-muted text-sm">
              La cuenta {code} no existe en el plan de este emisor.
            </div>
          ) : loading || !data ? (
            <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Cargando…
            </div>
          ) : data.rows.length === 0 ? (
            <div className="px-5 py-10 text-center t-muted text-sm">
              La cuenta {code} no tiene apuntes en {year}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-base">
                    <th className={th}>Fecha</th>
                    <th className={th}>Asiento</th>
                    <th className={th}>Concepto</th>
                    <th className={thR}>Debe</th>
                    <th className={thR}>Haber</th>
                    <th className={thR}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.openingBalanceCents !== 0 && (
                    <tr className="border-b border-base/50 italic t-muted">
                      <td className={td} colSpan={5}>
                        Saldo anterior
                      </td>
                      <td className={tdR}>{side(data.openingBalanceCents)}</td>
                    </tr>
                  )}
                  {data.rows.map((r, i) => (
                    <tr
                      key={`${r.entryId}-${i}`}
                      className="border-b border-base/50 hover:bg-hover/40"
                    >
                      <td className={`${td} whitespace-nowrap`}>
                        {new Date(r.date).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
                      </td>
                      <td className={`${td} font-mono`}>
                        <Link to="/ctb/journal" className="hover:underline text-toga-300">
                          {r.entryNumber ?? '—'}
                        </Link>
                      </td>
                      <td className={td}>{r.concept}</td>
                      <td className={tdR}>{r.debitCents ? formatCents(r.debitCents) : ''}</td>
                      <td className={tdR}>{r.creditCents ? formatCents(r.creditCents) : ''}</td>
                      <td className={`${tdR} font-semibold`}>{side(r.runningBalanceCents)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-base font-semibold">
                    <td className={td} colSpan={3}>
                      Totales
                    </td>
                    <td className={tdR}>{formatCents(data.totals.debitCents)}</td>
                    <td className={tdR}>{formatCents(data.totals.creditCents)}</td>
                    <td className={tdR}>{side(data.totals.balanceCents)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <Note>
            Solo cuentan los asientos contabilizados. Un contra-asiento aparece como un apunte más,
            al revés del original.
          </Note>
        </section>
      )}
    </div>
  )
}
